import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CommunityArticleResponse } from "../../../@apis/dto/response/community.response";
import { formatKoreanDate } from "../../../lib/utils/dateUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../lib/utils/cn";

export const CommunityCardVariants = cva("w-full rounded-[15px] p-5", {
  variants: {
    variant: {
      white: "bg-white",
      gray: "bg-[#f5f5f5]",
    },
  },
  defaultVariants: {},
});

interface Props
  extends
    React.ComponentProps<"div">,
    VariantProps<typeof CommunityCardVariants> {
  item: CommunityArticleResponse;
}

export default function CommunityCard({ item, variant, ...props }: Props) {
  const navigate = useNavigate();
  const metaRef = useRef<HTMLDivElement>(null);
  const nicknameRef = useRef<HTMLParagraphElement>(null);
  const dateRef = useRef<HTMLParagraphElement>(null);
  const [isWrapped, setIsWrapped] = useState(false);

  useEffect(() => {
    const container = metaRef.current;
    const nickname = nicknameRef.current;
    const date = dateRef.current;
    if (!container || !nickname || !date) return;

    const checkWrap = () => {
      setIsWrapped(
        nickname.getBoundingClientRect().top !==
          date.getBoundingClientRect().top,
      );
    };

    checkWrap();
    const observer = new ResizeObserver(checkWrap);
    observer.observe(container);
    return () => observer.disconnect();
  }, [item.nickname, item.updateDt]);

  return (
    <div
      className={cn(CommunityCardVariants({ variant }), props.className)}
      onClick={() => navigate(`/community/${item.id}`)}
      {...props}
    >
      <div className="flex-row gap-[5px]">
        <img className="w-[0.75rem]" src="/icons/ic-flag.webp" />
        <p className="font-[500] text-xs">{item.golfParkName}</p>
      </div>
      <div
        ref={metaRef}
        className="py-[15px] border-b border-[#e6e6e6] flex-row gap-[10px] flex-wrap"
      >
        <p ref={nicknameRef} className="font-[500] text-sm whitespace-nowrap">
          {item.nickname}
        </p>
        {!isWrapped && <div className="w-[1px] h-3 bg-[#e6e6e6]" />}
        <p
          ref={dateRef}
          className="font-[500] text-[#888] text-sm whitespace-nowrap"
        >
          {formatKoreanDate(item.updateDt)}
        </p>
      </div>

      <div className="space-y-[14px]">
        <div className="mt-[14px]">
          <p className="font-[500]">{item.title}</p>
          <p className="font-[500] text-[#888] break-all line-clamp-3 text-sm">
            {item.content}
          </p>
        </div>

        {item.fileList && (
          <div className="grid grid-cols-2 gap-[10px]">
            {item.fileList.map(
              ({ fileUrl }, index) =>
                index < 2 && (
                  <img
                    key={index}
                    src={staticURL(fileUrl)}
                    className="bg-gray-300 rounded-[10px] size-full aspect-square object-cover object-center"
                  />
                ),
            )}
          </div>
        )}

        <div className="flex-row gap-[10px]">
          <div className="flex-row gap-[5px]">
            <img className="w-[0.9rem]" src="/icons/ic-heart.webp" />
            <span className="font-[500] text-[#888] text-sm">
              {item.likeCount}개
            </span>
          </div>
          <div className="h-[10px] w-[1px] border-l border-[#e6e6e6]" />
          <div className="flex-row gap-[5px]">
            <img className="w-[0.9rem]" src="/icons/ic-comment.webp" />
            <span className="font-[500] text-[#888] text-sm">
              {item.commentCount}개
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
